import { getFormattedUnit } from "config";
import ExcelJS from "exceljs";

const htmlToText = (html) => {
  if (!html) return "-";

  const textarea = document.createElement("textarea");
  textarea.innerHTML = html;
  const decoded = textarea.value;

  return (
    decoded
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/?p>/gi, "\n")
      .replace(/<\/?div>/gi, "\n")
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/\n{3,}/g, "\n\n")
      .trim() || "-"
  );
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("en-GB");
};

const getCleanRemarkText = (value) => {
  const text = htmlToText(value);
  return text === "-" ? "" : text;
};

const getNegotiationOutcome = (supplier) => {
  const quotationSets = supplier?.vendorQuotations?.quotationSets || [];
  const firstQuotation = quotationSets[0];
  const lastQuotation = quotationSets[quotationSets.length - 1];
  const firstTotal =
    (firstQuotation?.totalWithoutGst || 0) +
    (firstQuotation?.othersTotalWithoutGst || 0);
  const lastTotal =
    (lastQuotation?.totalWithoutGst || 0) +
    (lastQuotation?.othersTotalWithoutGst || 0);

  if (firstTotal === lastTotal) return "Not Updated";

  const difference = firstTotal - lastTotal;
  const negotiationPercentage =
    firstTotal > 0 ? ((difference / firstTotal) * 100).toFixed(2) : "0.00";

  return `Rs. ${new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
  }).format(difference)} (${negotiationPercentage}%)`;
};

const applyAutoRowHeight = (
  sheet,
  rowIndex,
  text,
  { mergedCols = 3, columnWidth = 15, minHeight = 22, lineHeight = 15 } = {},
) => {
  if (!text || text === "-") return;

  const approxCharsPerLine = Math.max(
    18,
    Math.floor(columnWidth * mergedCols * 1.2),
  );
  const lineCount = String(text)
    .split("\n")
    .reduce(
      (total, line) => total + Math.max(1, Math.ceil(line.length / approxCharsPerLine)),
      0,
    );

  sheet.getRow(rowIndex).height = Math.max(minHeight, lineCount * lineHeight);
};

const getWrappedTextHeight = (
  texts,
  { mergedCols = 3, columnWidth = 15, minHeight = 24, lineHeight = 16 } = {},
) => {
  const approxCharsPerLine = Math.max(
    20,
    Math.floor(columnWidth * mergedCols * 1.15),
  );

  const lineCount = texts.reduce((maxLines, text) => {
    const currentLines = String(text || "")
      .split("\n")
      .reduce(
        (total, line) => total + Math.max(1, Math.ceil(line.length / approxCharsPerLine)),
        0,
      );

    return Math.max(maxLines, currentLines);
  }, 1);

  return Math.max(minHeight, lineCount * lineHeight + 8);
};

const setAmountCell = (cell, value) => {
  if (value === null || value === undefined || value === "" || Number.isNaN(Number(value))) {
    cell.value = "-";
    cell.alignment = { horizontal: "center", vertical: "middle" };
    return;
  }

  cell.value = Number(value);
  cell.numFmt = "#,##0.00";
  cell.alignment = { horizontal: "right", vertical: "middle" };
};

const getNumericAmount = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsedValue = Number(String(value).replace(/,/g, "").trim());
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const hasRateValue = (value) => {
  const numericValue = getNumericAmount(value);
  return numericValue !== null && numericValue !== 0;
};

const getCombinedRate = (item) => {
  const materialRate = getNumericAmount(item?.basePrice);
  const installationRate = getNumericAmount(item?.installationRate);

  if (materialRate === null && installationRate === null) return null;

  return (materialRate || 0) + (installationRate || 0);
};

const getValueBasedColumnWidth = (
  values,
  { minWidth, maxWidth, padding = 2 },
) => {
  const widestValue = values.reduce((widest, value) => {
    const longestLine = String(value ?? "")
      .split("\n")
      .reduce((length, line) => Math.max(length, line.length), 0);
    return Math.max(widest, longestLine);
  }, 0);

  return Math.min(maxWidth, Math.max(minWidth, widestValue + padding));
};

const getFormattedAmountForWidth = (value) => {
  const amount = getNumericAmount(value);
  if (amount === null) return "";

  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const getBudgetDeviationMeta = (budget, totalWithoutGst) => {
  if (
    typeof budget !== "number" ||
    Number.isNaN(budget) ||
    budget <= 0 ||
    typeof totalWithoutGst !== "number" ||
    Number.isNaN(totalWithoutGst)
  ) {
    return null;
  }

  const deviation = budget - totalWithoutGst;
  const percentage = Math.abs((deviation / budget) * 100);

  return {
    deviation,
    percentage,
    text: `Rs. ${new Intl.NumberFormat("en-IN").format(
      Math.abs(deviation),
    )} (${percentage.toFixed(2)}%) ${deviation < 0 ? "▲" : "▼"}`,
    color: deviation < 0 ? "FFFF0000" : "FF008000",
  };
};

const getLatestQuotationSet = (supplier) =>
  supplier?.vendorQuotations?.quotationSets?.slice(-1)?.[0] || null;

const getFirstQuotationSet = (supplier) =>
  supplier?.vendorQuotations?.quotationSets?.[0] || null;

const findByIdOrIndex = (items = [], referenceItem, fallbackIndex) => {
  const refId = referenceItem?._id || referenceItem?.id;
  if (refId) {
    const matchedItem = items.find((item) =>
      String(item?._id || item?.id) === String(refId),
    );

    if (matchedItem) return matchedItem;
  }

  return items?.[fallbackIndex] || null;
};

const getContractorSection = (quotationSet, referenceSection, sectionIndex) =>
  findByIdOrIndex(quotationSet?.contractorDetails || [], referenceSection, sectionIndex);

const getContractorScope = (
  quotationSet,
  referenceSection,
  sectionIndex,
  referenceScope,
  scopeIndex,
) => {
  const section = getContractorSection(quotationSet, referenceSection, sectionIndex);
  return findByIdOrIndex(section?.scopes || [], referenceScope, scopeIndex);
};

const getContractorMaterial = (
  quotationSet,
  referenceSection,
  sectionIndex,
  referenceScope,
  scopeIndex,
  referenceMaterial,
  materialIndex,
) => {
  const scope = getContractorScope(
    quotationSet,
    referenceSection,
    sectionIndex,
    referenceScope,
    scopeIndex,
  );

  return findByIdOrIndex(scope?.materials || [], referenceMaterial, materialIndex);
};

const getContractorRemarkText = (...values) => {
  for (const value of values) {
    const cleanedText = getCleanRemarkText(value);
    if (cleanedText) return cleanedText;
  }

  return "";
};

const buildContractorRateVisibility = (supplier) => {
  const latestSet = getLatestQuotationSet(supplier);
  const contractorDetails = latestSet?.contractorDetails || [];

  let showMaterialRate = false;
  let showInstallationRate = false;

  contractorDetails.forEach((section) => {
    if (hasRateValue(section?.basePrice)) showMaterialRate = true;
    if (hasRateValue(section?.installationRate)) showInstallationRate = true;

    (section?.scopes || []).forEach((scope) => {
      if (hasRateValue(scope?.basePrice)) showMaterialRate = true;
      if (hasRateValue(scope?.installationRate)) showInstallationRate = true;

      (scope?.materials || []).forEach((material) => {
        if (hasRateValue(material?.basePrice)) showMaterialRate = true;
        if (hasRateValue(material?.installationRate)) showInstallationRate = true;
      });
    });
  });

  return { showMaterialRate, showInstallationRate };
};

const buildVendorLayouts = ({ suppliers, startCol, mode, rateVisibilities }) => {
  let currentCol = startCol;

  return suppliers.map((supplier, supplierIndex) => {
    const visibility = rateVisibilities[supplierIndex] || {
      showMaterialRate: false,
      showInstallationRate: false,
    };

    let fields = [];
    if (mode === "summaryAmount") {
      fields = ["quotedAmount", "negotiatedAmount", "totalAmount"];
    } else if (mode === "summaryRateComparison") {
      fields =
        visibility.showMaterialRate && visibility.showInstallationRate
          ? [
              "quotedMaterialRate",
              "quotedInstallationRate",
              "negotiatedMaterialRate",
              "negotiatedInstallationRate",
              "totalExclGst",
            ]
          : ["quotedAmount", "negotiatedAmount", "totalExclGst"];
    } else if (mode === "summaryRate") {
      fields = [
        ...(visibility.showMaterialRate ? ["materialRate"] : []),
        ...(visibility.showInstallationRate ? ["installationRate"] : []),
        "totalExclGst",
      ];
    } else {
      fields = [
        "quotedMaterialRate",
        "quotedInstallationRate",
        "negotiatedMaterialRate",
        "negotiatedInstallationRate",
        "totalExclGst",
      ];
    }

    const fieldToCol = fields.reduce((accumulator, field, fieldIndex) => {
      accumulator[field] = currentCol + fieldIndex;
      return accumulator;
    }, {});

    const layout = {
      supplier,
      supplierIndex,
      colStart: currentCol,
      colEnd: currentCol + fields.length - 1,
      span: fields.length,
      fields,
      fieldToCol,
    };

    currentCol += fields.length;
    return layout;
  });
};

const getLayoutRange = (sheet, layout, rowNumber) => {
  const startLetter = sheet.getColumn(layout.colStart).letter;
  const endLetter = sheet.getColumn(layout.colEnd).letter;
  return `${startLetter}${rowNumber}:${endLetter}${rowNumber}`;
};

const applyGreyFill = (cell) => {
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFD9D9D9" },
  };
};

const applyGreyRowStyle = (row, fromCol, toCol) => {
  for (let col = fromCol; col <= toCol; col += 1) {
    const cell = row.getCell(col);
    cell.font = { bold: true };
    applyGreyFill(cell);
    cell.alignment = {
      horizontal: "center",
      vertical: "middle",
      wrapText: true,
    };
  }
};

const applyLightGreyRowStyle = (row, fromCol, toCol) => {
  for (let col = fromCol; col <= toCol; col += 1) {
    row.getCell(col).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFF3F4F6" },
    };
  }
};

const applyRightBorder = (cell) => {
  cell.border = {
    ...(cell.border || {}),
    right: { style: "medium", color: { argb: "FF808080" } },
  };
};

const applyBottomBorder = (cell) => {
  cell.border = {
    ...(cell.border || {}),
    bottom: { style: "thin", color: { argb: "FF000000" } },
  };
};

const addVerticalSeparators = (sheet, lastRow, staticEndCol, layouts) => {
  for (let rowIndex = 1; rowIndex <= lastRow; rowIndex += 1) {
    applyRightBorder(sheet.getRow(rowIndex).getCell(staticEndCol));
  }

  layouts.forEach((layout) => {
    for (let rowIndex = 1; rowIndex <= lastRow; rowIndex += 1) {
      applyRightBorder(sheet.getRow(rowIndex).getCell(layout.colEnd));
    }
  });
};

export const handleDownloadContractorComparisonExcel = async (
  rfqData,
  setExcelLoading,
  setLoadingMessage,
  enqueueSnackbar,
) => {
  const highlightCell = (cell) => {
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFD6F5D6" },
    };
  };

  const chargeLabels = [
    { label: "Transport Charges :", key: "transport" },
    { label: "Mathade Charges :", key: "mathadeCharges" },
    { label: "Loading Charges :", key: "loading" },
    { label: "Unloading Charges :", key: "unLoading" },
    { label: "Miscellaneous Charges :", key: "miscCharges" },
  ];

  try {
    const allSuppliers = rfqData?.suppliers || [];

    const suppliersWithQuotations = allSuppliers.filter(
      (supplier) => supplier?.quotationReceived,
    );

    const suppliersWithoutQuotations = allSuppliers.filter(
      (supplier) => !supplier?.quotationReceived,
    );

    const sortedSuppliersWithQuotes = [...suppliersWithQuotations].sort((a, b) => {
      const rankA = a?.vendorQuotations?.rank;
      const rankB = b?.vendorQuotations?.rank;

      if (!rankA || rankA === "NA") return 1;
      if (!rankB || rankB === "NA") return -1;

      const numberA = parseInt(rankA.replace(/\D/g, ""), 10);
      const numberB = parseInt(rankB.replace(/\D/g, ""), 10);

      return numberA - numberB;
    });

    const sortedSuppliers = [
      ...sortedSuppliersWithQuotes,
      ...suppliersWithoutQuotations,
    ];

    const l1Total =
      Math.min(
        ...sortedSuppliers
          .filter((supplier) => supplier?.quotationReceived)
          .map(
            (supplier) =>
              supplier?.vendorQuotations?.grandTotalWithoutGst ?? Infinity,
          ),
      ) || 0;

    const rateVisibilities = sortedSuppliers.map(buildContractorRateVisibility);
    const contractorSections = rfqData?.providingFixingDetails || [];
    const contractorScopes = contractorSections.flatMap((section) => section?.scopes || []);
    const contractorMaterials = contractorScopes.flatMap((scope) => scope?.materials || []);
    const descriptionValues = [
      ...contractorSections.map((section) => htmlToText(section?.sectionName)),
      ...contractorScopes.map((scope) => htmlToText(scope?.scopeOfWork)),
      ...contractorMaterials.map((material) => htmlToText(material?.name)),
    ];
    const budgetAmountValues = [
      rfqData?.rfqBudget,
      ...contractorScopes.map((scope) => scope?.budgetAmount),
      ...contractorMaterials.map((material) => material?.budgetAmount),
    ];
    const lowestAmountValues = [
      ...budgetAmountValues,
      ...sortedSuppliers.map(
        (supplier) => supplier?.vendorQuotations?.grandTotalWithoutGst,
      ),
    ];
    const staticColumnWidths = {
      description: getValueBasedColumnWidth(descriptionValues, {
        minWidth: 22,
        maxWidth: 32,
      }),
      budgetAmount: getValueBasedColumnWidth(
        budgetAmountValues.map(getFormattedAmountForWidth),
        { minWidth: 14, maxWidth: 16 },
      ),
      lowestAmount: getValueBasedColumnWidth(
        lowestAmountValues.map(getFormattedAmountForWidth),
        { minWidth: 14, maxWidth: 16 },
      ),
    };

    setExcelLoading(true);
    setLoadingMessage("Generating Contractor Comparison Excel...");

    const workbook = new ExcelJS.Workbook();

    const buildContractorSummarySheet = (sheetName, summaryMode) => {
      const summarySheet = workbook.addWorksheet(sheetName);
      summarySheet.columns = [
        { width: 5 },
        { width: staticColumnWidths.description },
        { width: 12 },
        { width: 10 },
        { width: 15 },
        { width: staticColumnWidths.budgetAmount },
        { width: staticColumnWidths.lowestAmount },
      ];

      const vendorLayouts = buildVendorLayouts({
        suppliers: sortedSuppliers,
        startCol: 8,
        mode:
          summaryMode === "amountComparison"
            ? "summaryAmount"
            : summaryMode === "rateComparison"
              ? "summaryRateComparison"
              : "summaryRate",
        rateVisibilities,
      });
      const summaryEndCol = vendorLayouts[vendorLayouts.length - 1]?.colEnd || 7;

      vendorLayouts.forEach((layout) => {
        layout.fields.forEach((field, fieldIndex) => {
          const columnIndex = layout.colStart + fieldIndex;
          if (field === "totalAmount" || field === "totalExclGst") {
            summarySheet.getColumn(columnIndex).width = 18;
          } else {
            summarySheet.getColumn(columnIndex).width = 14;
          }
        });
      });

      [
        [
          `Project Name: ${rfqData?.projectName || "-"}`,
          `RFQ ID: ${rfqData?.rfqId || "-"}`,
        ],
        [
          `RFQ Name: ${rfqData?.name || "-"}`,
          `Created By: ${rfqData?.requesterName || "-"}`,
        ],
        [
          "RFQ Type: Contractor",
          `Created At: ${formatDate(rfqData?.createdAt)}`,
        ],
      ].forEach(([leftValue, rightValue], index) => {
        const rowNumber = index + 1;
        summarySheet.mergeCells(`A${rowNumber}:D${rowNumber}`);
        summarySheet.mergeCells(`E${rowNumber}:G${rowNumber}`);

        const leftCell = summarySheet.getCell(`A${rowNumber}`);
        leftCell.value = leftValue;
        leftCell.font = { bold: true };
        leftCell.alignment = {
          vertical: "middle",
          horizontal: "left",
          wrapText: true,
        };

        const rightCell = summarySheet.getCell(`E${rowNumber}`);
        rightCell.value = rightValue;
        rightCell.font = { bold: true };
        rightCell.alignment = {
          vertical: "middle",
          horizontal: "left",
          wrapText: true,
        };

        summarySheet.getRow(rowNumber).height = 22;
      });

      vendorLayouts.forEach((layout) => {
        const vendorStartCol = summarySheet.getColumn(layout.colStart).letter;
        const vendorEndCol = summarySheet.getColumn(layout.colEnd).letter;
        const primaryContact = layout.supplier?.contactDetails?.[0] || {};
        const businessNameCell = summarySheet.getCell(`${vendorStartCol}1`);
        const contactCell = summarySheet.getCell(`${vendorStartCol}2`);

        summarySheet.mergeCells(`${vendorStartCol}1:${vendorEndCol}1`);
        summarySheet.mergeCells(`${vendorStartCol}2:${vendorEndCol}2`);

        businessNameCell.value = `${layout.supplier?.businessName || "-"} (${
          layout.supplier?.vendorQuotations?.rank || "N/A"
        })`;
        contactCell.value = `${primaryContact.mobile || "-"} | ${
          primaryContact.email || "-"
        }`;

        [businessNameCell, contactCell].forEach((cell) => {
          cell.font = { bold: true };
          cell.alignment = {
            vertical: "middle",
            horizontal: "center",
            wrapText: true,
          };
        });
      });

      summarySheet.mergeCells("A4:D4");
      summarySheet.mergeCells("E4:G4");
      const indentCell = summarySheet.getCell("A4");
      indentCell.value = `Indent No.: ${rfqData?.parentsRefId || "-"}`;
      indentCell.font = { bold: true };
      indentCell.alignment = { horizontal: "left", vertical: "middle" };

      const budgetCell = summarySheet.getCell("E4");
      budgetCell.value = `RFQ Budget: Rs. ${new Intl.NumberFormat("en-IN").format(
        rfqData?.rfqBudget || 0,
      )}`;
      budgetCell.font = { bold: true };
      budgetCell.alignment = { horizontal: "left", vertical: "middle" };
      summarySheet.getRow(4).height = 24;

      vendorLayouts.forEach((layout) => {
        summarySheet.mergeCells(getLayoutRange(summarySheet, layout, 4));
        const cell = summarySheet.getCell(
          `${summarySheet.getColumn(layout.colStart).letter}4`,
        );

        if (!layout.supplier?.quotationReceived) {
          cell.value = "";
          cell.font = {};
        } else {
          const deviationMeta = getBudgetDeviationMeta(
            rfqData?.rfqBudget,
            layout.supplier?.vendorQuotations?.grandTotalWithoutGst,
          );

          if (deviationMeta) {
            cell.value = deviationMeta.text;
            cell.font = {
              bold: true,
              color: { argb: deviationMeta.color },
            };
          } else {
            cell.value = "";
            cell.font = {};
          }
        }

        cell.alignment = {
          horizontal: "center",
          vertical: "middle",
          wrapText: true,
        };
      });

      const summaryHeaderRow = summarySheet.getRow(5);
      summaryHeaderRow.values = [
        "Sr.",
        "Description",
        "Quantity",
        "Unit",
        "Budget",
        "Budget\nAmount",
        "Lowest\nAmount",
      ];
      summaryHeaderRow.height = 42;
      applyGreyRowStyle(summaryHeaderRow, 1, 7);

      const summaryFieldLabels =
        summaryMode === "amountComparison"
          ? {
              quotedAmount: "Quoted\nAmount",
              negotiatedAmount: "Negotiated\nAmount",
              totalAmount: "Total\nAmount",
            }
          : summaryMode === "rateComparison"
            ? {
                quotedAmount: "Quoted\nRate",
                negotiatedAmount: "Negotiated\nRate",
                quotedMaterialRate: "Quoted Material\nRate",
                quotedInstallationRate: "Quoted Installation\nRate",
                negotiatedMaterialRate: "Negotiated Material\nRate",
                negotiatedInstallationRate: "Negotiated Installation\nRate",
                totalExclGst: "Total\nAmount",
              }
            : {
              materialRate: "Material\nRate",
              installationRate: "Installation\nRate",
              totalExclGst: "Total\n(Excl. GST)",
            };

      vendorLayouts.forEach((layout) => {
        layout.fields.forEach((field) => {
          const headerCell = summaryHeaderRow.getCell(layout.fieldToCol[field]);
          headerCell.value = summaryFieldLabels[field];
          headerCell.font = { bold: true };
          applyGreyFill(headerCell);
          headerCell.alignment = {
            horizontal: "center",
            vertical: "middle",
            wrapText: true,
          };
        });
      });

      let summaryRow = 6;

      const addSummaryRemarksRow = (remarksBySupplier, label = "") => {
        const validRemarks = remarksBySupplier.filter(({ text }) => !!text);
        if (validRemarks.length === 0) return;

        const remarksRow = summarySheet.getRow(summaryRow++);

        if (label) {
          summarySheet.mergeCells(`A${remarksRow.number}:G${remarksRow.number}`);
          const labelCell = summarySheet.getCell(`A${remarksRow.number}`);
          labelCell.value = label;
          labelCell.font = { italic: true };
          labelCell.alignment = {
            horizontal: "right",
            vertical: "top",
          };
        }

        vendorLayouts.forEach((layout) => {
          summarySheet.mergeCells(getLayoutRange(summarySheet, layout, remarksRow.number));
          const cell = summarySheet.getCell(
            `${summarySheet.getColumn(layout.colStart).letter}${remarksRow.number}`,
          );
          const matchedRemark = remarksBySupplier.find(
            ({ supplierIndex }) => supplierIndex === layout.supplierIndex,
          );
          cell.value = matchedRemark?.text || "";
          cell.font = { italic: true };
          cell.alignment = {
            horizontal: "left",
            vertical: "top",
            wrapText: true,
          };
        });

        remarksRow.height = getWrappedTextHeight(
          validRemarks.map(({ text }) => text),
          {
            mergedCols: Math.max(...vendorLayouts.map((layout) => layout.span), 1),
            columnWidth: 12,
            minHeight: 28,
            lineHeight: 18,
          },
        );
      };

      const setSummaryVendorValues = ({
        row,
        layout,
        firstItem,
        finalItem,
        quantitySource,
      }) => {
        if (!layout.supplier?.quotationReceived) {
          layout.fields.forEach((field) => {
            row.getCell(layout.fieldToCol[field]).value = "-";
            row.getCell(layout.fieldToCol[field]).alignment = {
              horizontal: "center",
              vertical: "middle",
            };
          });
          return null;
        }

        if (!finalItem) {
          layout.fields.forEach((field) => {
            row.getCell(layout.fieldToCol[field]).value = "-";
            row.getCell(layout.fieldToCol[field]).alignment = {
              horizontal: "center",
              vertical: "middle",
            };
          });
          return null;
        }

        if (summaryMode === "amountComparison") {
          const quotedRate = getCombinedRate(firstItem);
          const negotiatedRate = getCombinedRate(finalItem);
          const quantity =
            getNumericAmount(quantitySource?.quantity ?? finalItem?.quantity) || 0;
          const quotedAmount =
            quotedRate === null ? null : quotedRate * quantity;
          const negotiatedAmount =
            negotiatedRate === null ? null : negotiatedRate * quantity;
          const totalAmount =
            negotiatedAmount;

          setAmountCell(row.getCell(layout.fieldToCol.quotedAmount), quotedAmount);
          setAmountCell(
            row.getCell(layout.fieldToCol.negotiatedAmount),
            negotiatedAmount,
          );
          setAmountCell(row.getCell(layout.fieldToCol.totalAmount), totalAmount);

          return totalAmount;
        }

        if (summaryMode === "rateComparison") {
          if (layout.fieldToCol.quotedMaterialRate) {
            setAmountCell(
              row.getCell(layout.fieldToCol.quotedMaterialRate),
              firstItem?.basePrice,
            );
            setAmountCell(
              row.getCell(layout.fieldToCol.quotedInstallationRate),
              firstItem?.installationRate,
            );
            setAmountCell(
              row.getCell(layout.fieldToCol.negotiatedMaterialRate),
              finalItem?.basePrice,
            );
            setAmountCell(
              row.getCell(layout.fieldToCol.negotiatedInstallationRate),
              finalItem?.installationRate,
            );
          } else {
            setAmountCell(
              row.getCell(layout.fieldToCol.quotedAmount),
              getCombinedRate(firstItem),
            );
            setAmountCell(
              row.getCell(layout.fieldToCol.negotiatedAmount),
              getCombinedRate(finalItem),
            );
          }
          setAmountCell(
            row.getCell(layout.fieldToCol.totalExclGst),
            finalItem?.totalExclGst,
          );

          return getNumericAmount(finalItem?.totalExclGst);
        }

        if (layout.fieldToCol.materialRate) {
          setAmountCell(row.getCell(layout.fieldToCol.materialRate), finalItem?.basePrice);
        }
        if (layout.fieldToCol.installationRate) {
          setAmountCell(
            row.getCell(layout.fieldToCol.installationRate),
            finalItem?.installationRate,
          );
        }
        setAmountCell(
          row.getCell(layout.fieldToCol.totalExclGst),
          finalItem?.totalExclGst,
        );

        return getNumericAmount(finalItem?.totalExclGst);
      };

      rfqData?.providingFixingDetails?.forEach((section, sectionIndex) => {
        const sectionRow = summarySheet.getRow(summaryRow++);
        sectionRow.getCell(1).value = `S${sectionIndex + 1}`;
        sectionRow.getCell(2).value = htmlToText(section?.sectionName);
        sectionRow.font = { bold: true };

        section?.scopes?.forEach((scope, scopeIndex) => {
          const scopeRow = summarySheet.getRow(summaryRow++);
          scopeRow.getCell(1).value = scopeIndex + 1;
          const scopeText = htmlToText(scope?.scopeOfWork);
          scopeRow.getCell(2).value = scopeText;
          scopeRow.getCell(2).alignment = {
            horizontal: "left",
            vertical: "top",
            wrapText: true,
          };
          scopeRow.getCell(3).value = scope?.quantity;
          scopeRow.getCell(4).value = getFormattedUnit(scope?.unit);
          setAmountCell(scopeRow.getCell(5), scope?.budget);
          setAmountCell(scopeRow.getCell(6), scope?.budgetAmount);
          applyAutoRowHeight(summarySheet, scopeRow.number, scopeText, {
            mergedCols: 1,
            columnWidth: 45,
            minHeight: 28,
            lineHeight: 16,
          });

          const scopeTotals = [];
          vendorLayouts.forEach((layout) => {
            const lastScope = getContractorScope(
              getLatestQuotationSet(layout.supplier),
              section,
              sectionIndex,
              scope,
              scopeIndex,
            );
            const firstScope = getContractorScope(
              getFirstQuotationSet(layout.supplier),
              section,
              sectionIndex,
              scope,
              scopeIndex,
            );

            const totalAmount = setSummaryVendorValues({
              row: scopeRow,
              layout,
              firstItem: firstScope,
              finalItem: lastScope,
              quantitySource: scope,
            });

            if (typeof totalAmount === "number") {
              scopeTotals.push({ totalAmount, layout });
            }
          });

          if (scopeTotals.length > 0) {
            const lowestTotal = Math.min(
              ...scopeTotals.map(({ totalAmount }) => totalAmount),
            );
            scopeTotals.forEach(({ totalAmount, layout }) => {
              const totalField =
                summaryMode === "amountComparison" ? "totalAmount" : "totalExclGst";
              if (totalAmount === lowestTotal) {
                highlightCell(scopeRow.getCell(layout.fieldToCol[totalField]));
              }
            });
            setAmountCell(scopeRow.getCell(7), lowestTotal);
          } else {
            scopeRow.getCell(7).value = "-";
          }

          addSummaryRemarksRow(
            vendorLayouts.map((layout) => {
              const scopeData = getContractorScope(
                getLatestQuotationSet(layout.supplier),
                section,
                sectionIndex,
                scope,
                scopeIndex,
              );
              return {
                supplierIndex: layout.supplierIndex,
                text: getContractorRemarkText(
                  scopeData?.comments,
                  scopeData?.remarks,
                  scopeData?.remark,
                ),
              };
            }),
          );

          scope?.materials?.forEach((material, materialIndex) => {
            const materialRow = summarySheet.getRow(summaryRow++);
            materialRow.getCell(1).value = `${scopeIndex + 1}.${materialIndex + 1}`;
            materialRow.getCell(2).value = htmlToText(material?.name);
            materialRow.getCell(3).value = material?.quantity;
            materialRow.getCell(4).value = getFormattedUnit(material?.unit);
            setAmountCell(materialRow.getCell(5), material?.budget);
            setAmountCell(materialRow.getCell(6), material?.budgetAmount);

            const materialTotals = [];
            vendorLayouts.forEach((layout) => {
              const lastMaterial = getContractorMaterial(
                getLatestQuotationSet(layout.supplier),
                section,
                sectionIndex,
                scope,
                scopeIndex,
                material,
                materialIndex,
              );
              const firstMaterial = getContractorMaterial(
                getFirstQuotationSet(layout.supplier),
                section,
                sectionIndex,
                scope,
                scopeIndex,
                material,
                materialIndex,
              );

              const totalAmount = setSummaryVendorValues({
                row: materialRow,
                layout,
                firstItem: firstMaterial,
                finalItem: lastMaterial,
                quantitySource: material,
              });

              if (typeof totalAmount === "number") {
                materialTotals.push({ totalAmount, layout });
              }
            });

            if (materialTotals.length > 0) {
              const lowestTotal = Math.min(
                ...materialTotals.map(({ totalAmount }) => totalAmount),
              );
              materialTotals.forEach(({ totalAmount, layout }) => {
                const totalField =
                  summaryMode === "amountComparison" ? "totalAmount" : "totalExclGst";
                if (totalAmount === lowestTotal) {
                  highlightCell(materialRow.getCell(layout.fieldToCol[totalField]));
                }
              });
              setAmountCell(materialRow.getCell(7), lowestTotal);
            } else {
              materialRow.getCell(7).value = "-";
            }

            addSummaryRemarksRow(
              vendorLayouts.map((layout) => {
                const materialData = getContractorMaterial(
                  getLatestQuotationSet(layout.supplier),
                  section,
                  sectionIndex,
                  scope,
                  scopeIndex,
                  material,
                  materialIndex,
                );

                return {
                  supplierIndex: layout.supplierIndex,
                  text: getContractorRemarkText(
                    materialData?.remarks,
                    materialData?.remark,
                    materialData?.comments,
                  ),
                };
              }),
            );
          });
        });

        const sectionTotalRow = summarySheet.getRow(summaryRow++);
        sectionTotalRow.getCell(7).value = "Total";
        sectionTotalRow.getCell(7).font = { bold: true };
        sectionTotalRow.getCell(7).alignment = {
          horizontal: "right",
          vertical: "middle",
        };

        vendorLayouts.forEach((layout) => {
          summarySheet.mergeCells(getLayoutRange(summarySheet, layout, sectionTotalRow.number));
          const cell = summarySheet.getCell(
            `${summarySheet.getColumn(layout.colStart).letter}${sectionTotalRow.number}`,
          );

          if (!layout.supplier?.quotationReceived) {
            cell.value = "-";
            cell.alignment = { horizontal: "center", vertical: "middle" };
            return;
          }

          const sectionData = getContractorSection(
            getLatestQuotationSet(layout.supplier),
            section,
            sectionIndex,
          );
          setAmountCell(cell, sectionData?.sectionTotalExclGst);
          cell.font = { bold: true };
        });

        applyLightGreyRowStyle(sectionTotalRow, 1, summaryEndCol);
      });

      const addSummaryRow = (label, valueGetter, isText = false, applyGrey = false) => {
        summarySheet.mergeCells(`A${summaryRow}:G${summaryRow}`);
        const labelCell = summarySheet.getCell(`A${summaryRow}`);
        labelCell.value = label;
        labelCell.font = { bold: true };
        labelCell.alignment = {
          horizontal: "right",
          vertical: "right",
        };

        vendorLayouts.forEach((layout) => {
          summarySheet.mergeCells(getLayoutRange(summarySheet, layout, summaryRow));
          const cell = summarySheet.getCell(
            `${summarySheet.getColumn(layout.colStart).letter}${summaryRow}`,
          );

          if (!layout.supplier?.quotationReceived) {
            cell.value = isText ? "Quotation Not Submitted" : "-";
            cell.alignment = {
              horizontal: isText ? "left" : "center",
              vertical: "middle",
              wrapText: isText,
            };
            return;
          }

          const value = valueGetter(layout.supplier);
          if (isText) {
            cell.value = value || "-";
            cell.alignment = {
              horizontal: "right",
              vertical: "top",
              wrapText: true,
            };
          } else {
            setAmountCell(cell, value);
          }
        });

        if (applyGrey) {
          const row = summarySheet.getRow(summaryRow);
          for (let columnIndex = 1; columnIndex <= summaryEndCol; columnIndex += 1) {
            const cell = row.getCell(columnIndex);
            cell.font = { bold: true };
            applyGreyFill(cell);
          }
        }

        summaryRow += 1;
      };

      chargeLabels.forEach((charge) => {
        const chargeRemarks = vendorLayouts.map((layout) => ({
          supplierIndex: layout.supplierIndex,
          text: getContractorRemarkText(
            getLatestQuotationSet(layout.supplier)?.otherCharges?.[charge.key]?.remark,
          ),
        }));
        const hasAnyCharge = vendorLayouts.some((layout) => {
          if (!layout.supplier?.quotationReceived) return false;
          return (
            getLatestQuotationSet(layout.supplier)?.otherCharges?.[charge.key]?.mrp > 0
          );
        });

        if (!hasAnyCharge && !chargeRemarks.some(({ text }) => !!text)) return;

        addSummaryRow(
          charge.label,
          (supplier) => getLatestQuotationSet(supplier)?.otherCharges?.[charge.key]?.mrp ?? 0,
        );
        addSummaryRemarksRow(chargeRemarks, "Remarks :");
      });

      addSummaryRow(
        "Total Basic Amount",
        (supplier) => supplier?.vendorQuotations?.grandTotalWithoutGst,
        false,
        true,
      );

      addSummaryRow("Total GST", (supplier) => {
        const withGst = supplier?.vendorQuotations?.grandTotalWithGst ?? 0;
        const withoutGst = supplier?.vendorQuotations?.grandTotalWithoutGst ?? 0;
        return withGst - withoutGst;
      });

      addSummaryRow(
        "Total Quotation Value (Incl. GST)",
        (supplier) => supplier?.vendorQuotations?.grandTotalWithGst,
        false,
        true,
      );

      summarySheet.mergeCells(`A${summaryRow}:G${summaryRow}`);
      const differenceLabelCell = summarySheet.getCell(`A${summaryRow}`);
      differenceLabelCell.value = "Difference from Lowest Quote :";
      differenceLabelCell.font = { bold: true };
      differenceLabelCell.alignment = {
        horizontal: "right",
        vertical: "middle",
      };

      vendorLayouts.forEach((layout) => {
        summarySheet.mergeCells(getLayoutRange(summarySheet, layout, summaryRow));
        const cell = summarySheet.getCell(
          `${summarySheet.getColumn(layout.colStart).letter}${summaryRow}`,
        );

        if (!layout.supplier?.quotationReceived) {
          cell.value = "Quotation Not Submitted";
        } else if (
          layout.supplier?.vendorQuotations?.grandTotalWithoutGst === l1Total
        ) {
          cell.value = "Lowest Quote";
          cell.font = { italic: true };
        } else {
          const difference =
            (layout.supplier?.vendorQuotations?.grandTotalWithoutGst ?? 0) - l1Total;
          const percentage = l1Total > 0 ? (difference / l1Total) * 100 : 0;
          cell.value = `Rs. ${new Intl.NumberFormat("en-IN", {
            maximumFractionDigits: 2,
          }).format(difference)} (${percentage.toFixed(2)}%)`;
        }

        cell.alignment = {
          horizontal: "right",
          vertical: "middle",
          wrapText: true,
        };
      });
      summaryRow += 1;

      addSummaryRow("Negotiation Outcome :", getNegotiationOutcome, true,  "right");

      const hasPaymentTerms = vendorLayouts.some((layout) => {
        if (!layout.supplier?.quotationReceived) return false;
        const latestSet = getLatestQuotationSet(layout.supplier);
        const text = latestSet?.agreedToPaymentTerms
          ? htmlToText(rfqData?.paymentTerms)
          : htmlToText(latestSet?.counterPaymentTerms);
        return !!text && text !== "-";
      });

      if (hasPaymentTerms) {
        addSummaryRow(
          "Payment Terms :",
          (supplier) => {
            const latestSet = getLatestQuotationSet(supplier);
            return latestSet?.agreedToPaymentTerms
              ? htmlToText(rfqData?.paymentTerms)
              : htmlToText(latestSet?.counterPaymentTerms);
          },
          true,
        );
      }

      const hasVendorNotes = vendorLayouts.some((layout) => {
        if (!layout.supplier?.quotationReceived) return false;
        return !!getLatestQuotationSet(layout.supplier)?.notes;
      });

      if (hasVendorNotes) {
        addSummaryRow(
          "Vendor Notes :",
          (supplier) => getLatestQuotationSet(supplier)?.notes,
          true,
        );
      }

      const lastSummaryRow = summaryRow - 1;
      addVerticalSeparators(summarySheet, lastSummaryRow, 7, vendorLayouts);
      for (let columnIndex = 1; columnIndex <= summaryEndCol; columnIndex += 1) {
        applyBottomBorder(summarySheet.getRow(lastSummaryRow).getCell(columnIndex));
      }
    };

    // Create worksheets in the same order as their Excel tabs.
    buildContractorSummarySheet("Summary View 1", "rateComparison");

    const sheet = workbook.addWorksheet("Detailed View");
    sheet.columns = [
      { header: "Sr.", key: "sr", width: 5 },
      {
        header: "Description",
        key: "description",
        width: staticColumnWidths.description,
      },
      { header: "Quantity", key: "quantity", width: 12 },
      { header: "Unit", key: "unit", width: 10 },
      { header: "Budget", width: 15 },
      { header: "Budget\nAmount", width: staticColumnWidths.budgetAmount },
      { header: "Preferred\nBrands", key: "brands", width: 20 },
    ];

    const detailedVendorLayouts = buildVendorLayouts({
      suppliers: sortedSuppliers,
      startCol: 8,
      mode: "detailed",
      rateVisibilities,
    });
    const detailedEndCol =
      detailedVendorLayouts[detailedVendorLayouts.length - 1]?.colEnd || 7;

   // ---------- RFQ INFORMATION ----------
    sheet.mergeCells("A1:D1");
    sheet.mergeCells("E1:G1");

    sheet.getCell("A1").value =
      `Project Name: ${rfqData?.projectName || "-"}`;

    sheet.getCell("E1").value =
      `RFQ ID: ${rfqData?.rfqId || "-"}`;

    sheet.mergeCells("A2:D2");
    sheet.mergeCells("E2:G2");

    sheet.getCell("A2").value =
      `RFQ Name: ${rfqData?.name || "-"}`;

    sheet.getCell("E2").value =
      `Created By: ${rfqData?.requesterName || "-"}`;

    sheet.mergeCells("A3:D3");
    sheet.mergeCells("E3:G3");

    sheet.getCell("A3").value =
      `RFQ Type: Contractor`;

    sheet.getCell("E3").value =
      `Created At: ${formatDate(rfqData?.createdAt)}`;

    ["A1", "A2", "A3", "E1", "E2", "E3"].forEach((cell) => {
      sheet.getCell(cell).font = { bold: true };
      sheet.getCell(cell).alignment = {
        horizontal: "left",
        vertical: "middle",
        wrapText: true,
      };
    });

    // ---------- QUOTATION AND VENDOR COUNTS ----------
    sheet.mergeCells("A4:D4");
    sheet.mergeCells("E4:G4");

    sheet.getCell("A4").value = `Total Quotations: ${suppliersWithQuotations.length}`;
    sheet.getCell("E4").value = `Total Invited Vendors: ${allSuppliers.length}`;

    ["A4", "E4"].forEach((cell) => {
      sheet.getCell(cell).font = { bold: true };
      sheet.getCell(cell).alignment = {
        horizontal: "left",
        vertical: "middle",
      };
    });

    // ---------- INDENT + RFQ BUDGET ----------
    sheet.mergeCells("A5:D5");
    sheet.mergeCells("E5:G5");

    sheet.getCell("A5").value =
      `Indent No.: ${rfqData?.parentsRefId || "-"}`;

    sheet.getCell("E5").value =
      `RFQ Budget: Rs. ${new Intl.NumberFormat("en-IN").format(
        rfqData?.rfqBudget || 0,
      )}`;

    sheet.getCell("A5").font = { bold: true };
    sheet.getCell("E5").font = { bold: true };

    sheet.getCell("A5").alignment = {
      horizontal: "left",
      vertical: "middle",
    };

    sheet.getCell("E5").alignment = {
      horizontal: "left",
      vertical: "middle",
    };

    sheet.getRow(1).height = 22;
    sheet.getRow(2).height = 22;
    sheet.getRow(3).height = 22;
    sheet.getRow(4).height = 22;
    sheet.getRow(5).height = 24;

    detailedVendorLayouts.forEach((layout) => {
      const vendorStartCol = sheet.getColumn(layout.colStart).letter;
      const vendorEndCol = sheet.getColumn(layout.colEnd).letter;
      const primaryContact = layout.supplier?.contactDetails?.[0] || {};
      const businessNameCell = sheet.getCell(`${vendorStartCol}1`);
      const contactCell = sheet.getCell(`${vendorStartCol}2`);
      const deviationCell = sheet.getCell(`${vendorStartCol}5`);
      const deviationMeta = layout.supplier?.quotationReceived
        ? getBudgetDeviationMeta(
            rfqData?.rfqBudget,
            layout.supplier?.vendorQuotations?.grandTotalWithoutGst,
          )
        : null;

      sheet.mergeCells(`${vendorStartCol}1:${vendorEndCol}1`);
      sheet.mergeCells(`${vendorStartCol}2:${vendorEndCol}2`);
      sheet.mergeCells(`${vendorStartCol}5:${vendorEndCol}5`);

      businessNameCell.value = `${layout.supplier?.businessName || "-"} (${
        layout.supplier?.vendorQuotations?.rank || "N/A"
      })`;
      contactCell.value = `${primaryContact.mobile || "-"} | ${
        primaryContact.email || "-"
      }`;

      [businessNameCell, contactCell].forEach((cell) => {
        cell.font = { bold: true };
        cell.alignment = {
          horizontal: "center",
          vertical: "middle",
          wrapText: true,
        };
      });

      if (!layout.supplier?.quotationReceived) {
        deviationCell.value = "Quotation not submitted";
        deviationCell.font = { italic: true, color: { argb: "FF6B7280" } };
      } else if (deviationMeta) {
        deviationCell.value = deviationMeta.text;
        deviationCell.font = {
          bold: true,
          color: { argb: deviationMeta.color },
        };
      } else {
        deviationCell.value = "";
        deviationCell.font = { italic: true, color: { argb: "FF6B7280" } };
      }

      deviationCell.alignment = {
        horizontal: "center",
        vertical: "middle",
        wrapText: true,
      };
    });

    const detailedHeaderRow = sheet.getRow(6);
    detailedHeaderRow.values = [
      "Sr.",
      "Description",
      "Quantity",
      "Unit",
      "Budget",
      "Budget\nAmount",
      "Preferred\nBrands",
    ];

    const detailedFieldLabels = {
      quotedMaterialRate: "Quoted Material Rate",
      negotiatedMaterialRate: "Negotiated Material Rate",
      quotedInstallationRate: "Quoted Installation Rate",
      negotiatedInstallationRate: "Negotiated Installation Rate",
      totalExclGst: "Total Amount",
    };

    detailedVendorLayouts.forEach((layout) => {
      layout.fields.forEach((field, fieldIndex) => {
        const columnIndex = layout.colStart + fieldIndex;
        const headerCell = detailedHeaderRow.getCell(columnIndex);
        headerCell.value = detailedFieldLabels[field];
        if (field === "totalExclGst") {
          sheet.getColumn(columnIndex).width = 18;
        } else {
          sheet.getColumn(columnIndex).width = 14;
        }
      });
    });

    detailedHeaderRow.height = 42;
    applyGreyRowStyle(detailedHeaderRow, 1, detailedEndCol);

    const detailedRows = [];
    let currentDetailedRow = 7;

    const maybeAddVendorRemarkRow = (remarks) => {
      if (!remarks.some(Boolean)) return null;
      const remarkRowNumber = currentDetailedRow++;
      detailedRows.push({
        type: "remark",
        rowNumber: remarkRowNumber,
        remarks,
      });
      return remarkRowNumber;
    };

    rfqData?.providingFixingDetails?.forEach((section, sectionIndex) => {
      const sectionRowNumber = currentDetailedRow++;
      const sectionRow = sheet.getRow(sectionRowNumber);
      sectionRow.getCell(1).value = `S${sectionIndex + 1}`;
      sectionRow.getCell(2).value = htmlToText(section?.sectionName);
      sectionRow.font = { bold: true };
      detailedRows.push({
        type: "section",
        rowNumber: sectionRowNumber,
        section,
        sectionIndex,
      });

      maybeAddVendorRemarkRow(
        detailedVendorLayouts.map((layout) => {
          const sectionData = getContractorSection(
            getLatestQuotationSet(layout.supplier),
            section,
            sectionIndex,
          );
          return getContractorRemarkText(
            sectionData?.comments,
            sectionData?.remarks,
            sectionData?.remark,
          );
        }),
      );

      section?.scopes?.forEach((scope, scopeIndex) => {
        const scopeRowNumber = currentDetailedRow++;
        const scopeRow = sheet.getRow(scopeRowNumber);
        const scopeText = htmlToText(scope?.scopeOfWork);
        scopeRow.getCell(1).value = scopeIndex + 1;
        scopeRow.getCell(2).value = scopeText;
        scopeRow.getCell(2).alignment = {
          horizontal: "left",
          vertical: "top",
          wrapText: true,
        };
        scopeRow.getCell(3).value = scope?.quantity;
        scopeRow.getCell(4).value = getFormattedUnit(scope?.unit);
        setAmountCell(scopeRow.getCell(5), scope?.budget);
        setAmountCell(scopeRow.getCell(6), scope?.budgetAmount);
        scopeRow.getCell(7).value =
          scope?.preferredBrands?.join(", ")?.replace(/<[^>]*>/g, "") || "";
        applyAutoRowHeight(sheet, scopeRowNumber, scopeText, {
          mergedCols: 1,
          columnWidth: 45,
          minHeight: 28,
          lineHeight: 16,
        });

        detailedRows.push({
          type: "scope",
          rowNumber: scopeRowNumber,
          section,
          sectionIndex,
          scope,
          scopeIndex,
        });

        maybeAddVendorRemarkRow(
          detailedVendorLayouts.map((layout) => {
            const scopeData = getContractorScope(
              getLatestQuotationSet(layout.supplier),
              section,
              sectionIndex,
              scope,
              scopeIndex,
            );
            return getContractorRemarkText(
              scopeData?.comments,
              scopeData?.remarks,
              scopeData?.remark,
            );
          }),
        );

        scope?.materials?.forEach((material, materialIndex) => {
          const materialRowNumber = currentDetailedRow++;
          const materialRow = sheet.getRow(materialRowNumber);
          materialRow.getCell(1).value = `${scopeIndex + 1}.${materialIndex + 1}`;
          materialRow.getCell(2).value = htmlToText(material?.name);
          materialRow.getCell(3).value = material?.quantity;
          materialRow.getCell(4).value = getFormattedUnit(material?.unit);
          setAmountCell(materialRow.getCell(5), material?.budget);
          setAmountCell(materialRow.getCell(6), material?.budgetAmount);
          materialRow.getCell(7).value = material?.brands?.join(", ") || "";

          detailedRows.push({
            type: "material",
            rowNumber: materialRowNumber,
            section,
            sectionIndex,
            scope,
            scopeIndex,
            material,
            materialIndex,
          });

          maybeAddVendorRemarkRow(
            detailedVendorLayouts.map((layout) => {
              const materialData = getContractorMaterial(
                getLatestQuotationSet(layout.supplier),
                section,
                sectionIndex,
                scope,
                scopeIndex,
                material,
                materialIndex,
              );
              return getContractorRemarkText(
                materialData?.remarks,
                materialData?.remark,
                materialData?.comments,
              );
            }),
          );
        });
      });

      const sectionTotalRowNumber = currentDetailedRow++;
      const sectionTotalRow = sheet.getRow(sectionTotalRowNumber);
      sectionTotalRow.getCell(7).value = "Total";
      sectionTotalRow.getCell(7).font = { bold: true };
      sectionTotalRow.getCell(7).alignment = {
        horizontal: "right",
        vertical: "middle",
      };
      applyLightGreyRowStyle(sectionTotalRow, 1, detailedEndCol);

      detailedRows.push({
        type: "sectionTotal",
        rowNumber: sectionTotalRowNumber,
        section,
        sectionIndex,
      });
    });

    detailedVendorLayouts.forEach((layout) => {
      const firstQuotationSet = getFirstQuotationSet(layout.supplier);

      detailedRows.forEach((rowLayout) => {
        if (rowLayout.type === "remark") {
          sheet.mergeCells(getLayoutRange(sheet, layout, rowLayout.rowNumber));
          const remarkCell = sheet.getCell(
            `${sheet.getColumn(layout.colStart).letter}${rowLayout.rowNumber}`,
          );
          remarkCell.value = rowLayout.remarks[layout.supplierIndex] || "";
          remarkCell.font = { italic: true };
          remarkCell.alignment = {
            horizontal: "left",
            vertical: "top",
            wrapText: true,
          };
          return;
        }

        const row = sheet.getRow(rowLayout.rowNumber);

        if (rowLayout.type === "sectionTotal") {
          sheet.mergeCells(getLayoutRange(sheet, layout, rowLayout.rowNumber));
          const cell = sheet.getCell(
            `${sheet.getColumn(layout.colStart).letter}${rowLayout.rowNumber}`,
          );

          if (!layout.supplier?.quotationReceived) {
            cell.value = "-";
            cell.alignment = { horizontal: "center", vertical: "middle" };
          } else {
            const sectionData = getContractorSection(
              getLatestQuotationSet(layout.supplier),
              rowLayout.section,
              rowLayout.sectionIndex,
            );
            setAmountCell(cell, sectionData?.sectionTotalExclGst);
          }

          cell.font = { bold: true };
          return;
        }

        if (!layout.supplier?.quotationReceived) {
          layout.fields.forEach((field) => {
            row.getCell(layout.fieldToCol[field]).value = "-";
            row.getCell(layout.fieldToCol[field]).alignment = {
              horizontal: "center",
              vertical: "middle",
            };
          });
          return;
        }

        if (rowLayout.type === "section") {
          layout.fields.forEach((field) => {
            const cell = row.getCell(layout.fieldToCol[field]);
            cell.value = "";
          });
          return;
        }

        if (rowLayout.type === "scope") {
          const scopeData = getContractorScope(
            getLatestQuotationSet(layout.supplier),
            rowLayout.section,
            rowLayout.sectionIndex,
            rowLayout.scope,
            rowLayout.scopeIndex,
          );
          const firstScopeData = getContractorScope(
            firstQuotationSet,
            rowLayout.section,
            rowLayout.sectionIndex,
            rowLayout.scope,
            rowLayout.scopeIndex,
          );

          if (!scopeData) {
            layout.fields.forEach((field) => {
              row.getCell(layout.fieldToCol[field]).value = "-";
              row.getCell(layout.fieldToCol[field]).alignment = {
                horizontal: "center",
                vertical: "middle",
              };
            });
            return;
          }

          const hasMaterialChildren =
            (rowLayout.scope?.materials?.length || 0) > 0 ||
            (scopeData?.materials?.length || 0) > 0;

          layout.fields.forEach((field) => {
            const cell = row.getCell(layout.fieldToCol[field]);
            if (hasMaterialChildren) {
              cell.value = "";
              return;
            }

            if (field === "quotedMaterialRate") {
              setAmountCell(cell, firstScopeData?.basePrice);
              return;
            }
            if (field === "negotiatedMaterialRate") {
              setAmountCell(cell, scopeData?.basePrice);
              return;
            }
            if (field === "quotedInstallationRate") {
              setAmountCell(cell, firstScopeData?.installationRate);
              return;
            }
            if (field === "negotiatedInstallationRate") {
              setAmountCell(cell, scopeData?.installationRate);
              return;
            }
            if (field === "totalExclGst") {
              setAmountCell(cell, scopeData?.totalExclGst);
            }
          });
          return;
        }

        const materialData = getContractorMaterial(
          getLatestQuotationSet(layout.supplier),
          rowLayout.section,
          rowLayout.sectionIndex,
          rowLayout.scope,
          rowLayout.scopeIndex,
          rowLayout.material,
          rowLayout.materialIndex,
        );
        const firstMaterialData = getContractorMaterial(
          firstQuotationSet,
          rowLayout.section,
          rowLayout.sectionIndex,
          rowLayout.scope,
          rowLayout.scopeIndex,
          rowLayout.material,
          rowLayout.materialIndex,
        );

        if (!materialData) {
          layout.fields.forEach((field) => {
            row.getCell(layout.fieldToCol[field]).value = "-";
            row.getCell(layout.fieldToCol[field]).alignment = {
              horizontal: "center",
              vertical: "middle",
            };
          });
          return;
        }

        layout.fields.forEach((field) => {
          const cell = row.getCell(layout.fieldToCol[field]);
          if (field === "quotedMaterialRate") {
            setAmountCell(cell, firstMaterialData?.basePrice);
            return;
          }
          if (field === "negotiatedMaterialRate") {
            setAmountCell(cell, materialData?.basePrice);
            return;
          }
          if (field === "quotedInstallationRate") {
            setAmountCell(cell, firstMaterialData?.installationRate);
            return;
          }
          if (field === "negotiatedInstallationRate") {
            setAmountCell(cell, materialData?.installationRate);
            return;
          }
          if (field === "totalExclGst") {
            setAmountCell(cell, materialData?.totalExclGst);
          }
        });
      });
    });

    detailedRows
      .filter((rowLayout) => rowLayout.type === "remark")
      .forEach((rowLayout) => {
        sheet.mergeCells(`B${rowLayout.rowNumber}:G${rowLayout.rowNumber}`);
        const labelCell = sheet.getCell(`B${rowLayout.rowNumber}`);
        labelCell.font = { bold: true, italic: true };
        labelCell.alignment = {
          horizontal: "left",
          vertical: "top",
          wrapText: true,
        };
        sheet.getRow(rowLayout.rowNumber).height = getWrappedTextHeight(
          rowLayout.remarks,
          {
            mergedCols: Math.max(
              ...detailedVendorLayouts.map((layout) => layout.span),
              1,
            ),
            columnWidth: 12,
            minHeight: 32,
            lineHeight: 22,
          },
        );
      });

    let detailedSummaryRow = currentDetailedRow + 1;

    const addDetailedSummaryRow = (
      label,
      valueGetter,
      isText = false,
      textAlignment = "left",
    ) => {
      sheet.mergeCells(`A${detailedSummaryRow}:G${detailedSummaryRow}`);
      const labelCell = sheet.getCell(`A${detailedSummaryRow}`);
      labelCell.value = label;
      labelCell.font = { bold: true };
      labelCell.alignment = {
        horizontal: "right",
        vertical: "middle",
      };

      detailedVendorLayouts.forEach((layout) => {
        sheet.mergeCells(getLayoutRange(sheet, layout, detailedSummaryRow));
        const cell = sheet.getCell(
          `${sheet.getColumn(layout.colStart).letter}${detailedSummaryRow}`,
        );

        if (!layout.supplier?.quotationReceived) {
          cell.value = isText ? "Quotation Not Submitted" : "-";
        } else {
          const value = valueGetter(layout.supplier);
          if (isText) {
            cell.value = value || "-";
            cell.alignment = {
              horizontal: textAlignment,
              vertical: "top",
              wrapText: true,
            };
          } else {
            setAmountCell(cell, value);
          }
        }
      });

      detailedSummaryRow += 1;
    };

    chargeLabels.forEach((charge) => {
      const hasAnyCharge = detailedVendorLayouts.some((layout) => {
        if (!layout.supplier?.quotationReceived) return false;
        return (
          getLatestQuotationSet(layout.supplier)?.otherCharges?.[charge.key]?.mrp > 0
        );
      });

      if (!hasAnyCharge) return;

      addDetailedSummaryRow(
        charge.label,
        (supplier) => getLatestQuotationSet(supplier)?.otherCharges?.[charge.key]?.mrp ?? 0,
      );
    });

    addDetailedSummaryRow(
      "Total Basic Amount :",
      (supplier) => supplier?.vendorQuotations?.grandTotalWithoutGst,
    );

    addDetailedSummaryRow("Total GST :", (supplier) => {
      const withGst = supplier?.vendorQuotations?.grandTotalWithGst ?? 0;
      const withoutGst = supplier?.vendorQuotations?.grandTotalWithoutGst ?? 0;
      return withGst - withoutGst;
    });

    addDetailedSummaryRow(
      "Total Quotation Value (Incl. GST) :",
      (supplier) => supplier?.vendorQuotations?.grandTotalWithGst,
    );

    sheet.mergeCells(`A${detailedSummaryRow}:G${detailedSummaryRow}`);
    const detailedDifferenceLabel = sheet.getCell(`A${detailedSummaryRow}`);
    detailedDifferenceLabel.value = "Difference from Lowest Quote :";
    detailedDifferenceLabel.font = { bold: true };
    detailedDifferenceLabel.alignment = {
      horizontal: "right",
      vertical: "middle",
    };

    detailedVendorLayouts.forEach((layout) => {
      sheet.mergeCells(getLayoutRange(sheet, layout, detailedSummaryRow));
      const cell = sheet.getCell(
        `${sheet.getColumn(layout.colStart).letter}${detailedSummaryRow}`,
      );

      if (!layout.supplier?.quotationReceived) {
        cell.value = "Quotation Not Submitted";
      } else if (layout.supplier?.vendorQuotations?.grandTotalWithoutGst === l1Total) {
        cell.value = "Lowest Quote";
        cell.font = { italic: true };
      } else {
        const difference =
          (layout.supplier?.vendorQuotations?.grandTotalWithoutGst ?? 0) - l1Total;
        const percentage = l1Total ? (difference / l1Total) * 100 : 0;
        cell.value = `Rs. ${new Intl.NumberFormat("en-IN").format(
          difference,
        )} (${percentage.toFixed(2)}%)`;
      }

      cell.alignment = {
        horizontal: "right",
        vertical: "middle",
        wrapText: true,
      };
    });
    detailedSummaryRow += 1;

    addDetailedSummaryRow("Negotiation Outcome :", getNegotiationOutcome, true, "right");

    addDetailedSummaryRow(
      "Payment Terms :",
      (supplier) => {
        const latestSet = getLatestQuotationSet(supplier);
        return latestSet?.agreedToPaymentTerms
          ? htmlToText(rfqData?.paymentTerms)
          : htmlToText(latestSet?.counterPaymentTerms);
      },
      true,
    );

    addDetailedSummaryRow(
      "Vendor Notes :",
      (supplier) => getLatestQuotationSet(supplier)?.notes,
      true,
    );

    const lastDetailedRow = detailedSummaryRow - 1;
    addVerticalSeparators(sheet, lastDetailedRow, 7, detailedVendorLayouts);
    for (let columnIndex = 1; columnIndex <= detailedEndCol; columnIndex += 1) {
      applyBottomBorder(sheet.getRow(lastDetailedRow).getCell(columnIndex));
    }

    buildContractorSummarySheet("Summary View 2", "amountComparison");

    const termsSheet = workbook.addWorksheet("Terms of Contract");
    termsSheet.getCell("A1").value = "Terms of Contract";
    termsSheet.getCell("A1").font = { bold: true };
    termsSheet.getCell("A1").alignment = {
      vertical: "middle",
      horizontal: "center",
    };
    termsSheet.getCell("A2").value =
      rfqData?.termsAndConditions?.replace(/<[^>]*>/g, "") || "";
    termsSheet.getCell("A2").alignment = { wrapText: true };
    termsSheet.getColumn(1).width = 100;

    const blob = await workbook.xlsx.writeBuffer();
    const url = URL.createObjectURL(new Blob([blob]));
    const link = document.createElement("a");
    link.href = url;
    link.download = `${rfqData?.name?.substring(0, 25) || "Export"}_${
      rfqData?.rfqType || ""
    }_${rfqData?.rfqId || ""}.xlsx`;
    link.click();
    URL.revokeObjectURL(url);

    enqueueSnackbar("Excel downloaded successfully!", {
      variant: "success",
      autoHideDuration: 3000,
    });
  } catch (error) {
    console.error("Error generating contractor comparison Excel:", error);
    enqueueSnackbar("Failed to download Excel file.", {
      variant: "error",
      autoHideDuration: 3000,
    });
  } finally {
    setExcelLoading(false);
    setLoadingMessage("");
  }
};
